import { useTranslation } from 'react-i18next';
import { useCallback, useMemo } from 'react';
import styles from './index.module.css';
import React from 'react';
import cx from 'clsx';
import {
  IconRcOfferChargedMeIcon,
  IconRcOfferReturnedIcon,
  IconRcOfferUnchargedIcon,
  IconRcHiShareTwitter,
  IconRcHiBoost,
} from 'assets/hi';
import { useMessageAction } from '@/views/Chats/hooks/hiMessage';
import { HelperTooltip } from '@/components';
import { useHiChatScamMessageRead, useSwitch } from '@/hooks';
import { openPostTwitter } from '@/utils';

import { HiMessageLocal } from '../../../types';
import {
  parseHiMessageData,
  formatMessageDate,
  formatOfferPrice,
  SAY_HI_SHARE_LINK,
  formatOfferPriceNumber,
} from '../../../utils';
import { RichTextPreviewer } from '../../RichText/Previewer';
import { BoostTipModal } from '../BoostTipModal';
import { CommonPreviewer } from './components/CommonPreviewer';

export default function NormalMessageItem({
  messageInfo,
  otherSideAccountName,
  otherSideUser,
}: {
  messageInfo: HiMessageLocal;
  otherSideAccountName: string;
  otherSideUser?: UserInfo;
}) {
  const { t } = useTranslation();

  const isMe = messageInfo.attributes.is_from_me;
  const offer = messageInfo.offer;
  const { mutateAsync: operationMessage } = useMessageAction(messageInfo.id);
  const { putScamMessageRead, isScamMessageRead } = useHiChatScamMessageRead();
  const { on: boostShown, turnOn: showBoost, turnOff: hideBoost } = useSwitch();

  const offerTextStyles = useMemo(
    () => (isMe ? styles.myOfferInfoText : styles.offerInfoText),
    [isMe],
  );
  const messageActionHandler = useCallback(
    (action: 'click_image' | 'click_url') => {
      if (isMe) {
        return;
      }
      operationMessage(action);
    },
    [isMe, operationMessage],
  );
  const { type, value, url, image } = useMemo(
    () => parseHiMessageData(messageInfo.data),
    [messageInfo.data],
  );

  const { value: translatedContent } = useMemo(
    () => parseHiMessageData(messageInfo.data_translation),
    [messageInfo.data_translation],
  );

  const MessageContent = useMemo(() => {
    switch (type) {
      case 'richtext':
        return RichTextPreviewer({ content: value });
    }
    return (
      <CommonPreviewer
        value={value}
        translatedContent={translatedContent}
        url={url}
        image={image}
        handleElementAction={messageActionHandler}
        creatorId={messageInfo.sendor_id}
      />
    );
  }, [image, messageActionHandler, type, url, value, translatedContent, messageInfo.sendor_id]);

  const messageStatusText = useMemo(() => {
    if (!isMe) return;

    if (messageInfo.attributes.is_other_read) {
      if (!offer) {
        return t('NormalMessageItem.read');
      }

      switch (offer.status) {
        case 'uncharged':
          return t('NormalMessageItem.uncharged');
        case 'charged':
          return t('NormalMessageItem.charged');
        case 'expired':
          return t('NormalMessageItem.expired');
      }
    } else {
      if (!offer) {
        return t('NormalMessageItem.delivered');
      }

      switch (offer.status) {
        case 'uncharged':
          return t('NormalMessageItem.delivered');
        case 'charged':
          return t('NormalMessageItem.charged');
        case 'expired':
          return t('NormalMessageItem.expired');
      }
    }
  }, [isMe, messageInfo.attributes.is_other_read, offer, t]);

  const isScamMessage = useMemo(
    () => !isMe && messageInfo.attributes.is_scam,
    [isMe, messageInfo.attributes.is_scam],
  );

  const isScamMessageUnread = useMemo(
    () => isScamMessage && !isScamMessageRead(messageInfo.session_id, messageInfo.id),
    [isScamMessage, isScamMessageRead, messageInfo?.id, messageInfo?.session_id],
  );

  const readScamMessage = useCallback(() => {
    putScamMessageRead(messageInfo.session_id, messageInfo.id);
  }, [messageInfo.id, messageInfo.session_id, putScamMessageRead]);

  const showTwitterShare = useMemo(() => {
    return (
      isMe &&
      offer?.status === 'uncharged' &&
      !messageInfo.attributes.is_other_read &&
      otherSideUser?.twitter_id_verified
    );
  }, [
    isMe,
    messageInfo.attributes.is_other_read,
    offer?.status,
    otherSideUser?.twitter_id_verified,
  ]);

  const onShareTwitter = useCallback(() => {
    openPostTwitter(
      `@${otherSideUser?.twitter_id} Yo, I offered ${formatOfferPrice(
        offer!,
      )} to buy your attention, you click this link to go to "DeBank Hi" to read my message and earn it. ${SAY_HI_SHARE_LINK}/${
        messageInfo?.sendor_id
      }\n\n#DeBankHi`,
    );
  }, [messageInfo?.sendor_id, offer, otherSideUser?.twitter_id]);

  const handleBoostClick = useCallback(() => {
    showBoost();
  }, [showBoost]);

  return (
    <div className={cx(styles.messageItemWrapper, isMe && styles.myMessageItemWrapper)}>
      <div
        className={cx(
          styles.messageItem,
          isMe && styles.myMessageItem,
          isMe && offer?.status === 'expired' && styles.myExpiredMessageItem,
          styles[`messageTyped_${type}`],
          offer && styles[`messageStatus_${offer.status}`],
          isMe && styles[`messageTyped_${type}_me`],
        )}>
        {isMe && messageStatusText ? (
          <span className={styles.messageStatus}>
            {showTwitterShare && (
              <HelperTooltip title={t('NormalMessageItem.xNotify', { name: otherSideAccountName })}>
                <IconRcHiShareTwitter onClick={onShareTwitter} className={styles.twitterIcon} />
              </HelperTooltip>
            )}
            {messageStatusText}
          </span>
        ) : null}

        <div className={cx(isScamMessageUnread && styles.scamBlurContent)}>{MessageContent}</div>
        <div className={cx(styles.offerInfoItem, isScamMessageUnread && styles.scamBlurContent)}>
          <span className={offerTextStyles}>{formatMessageDate(messageInfo.sending_time)}</span>
          {offer ? (
            <HelperTooltip
              title={
                offer?.status === 'uncharged'
                  ? ''
                  : t('NormalMessageItem.offerStatus', {
                      context:
                        offer.status === 'expired' ? 'Returned' : isMe ? 'Accepted' : 'Earned',
                      date: formatMessageDate(offer?.updated_at),
                    })
              }
              placement="top">
              <span className={cx(offerTextStyles, styles.offerPrice)}>
                <span className={styles.offerPriceText}>
                  {formatOfferPrice(offer)}{' '}
                  {offer.bonus && isMe ? (
                    <HelperTooltip
                      title={t('NormalMessageItem.bonusTip', {
                        amount: formatOfferPriceNumber(offer.bonus, '$'),
                      })}
                      placement="top">
                      <span className={styles.offerBonus}>
                        {formatOfferPriceNumber(offer.bonus, '+ $')}
                      </span>
                    </HelperTooltip>
                  ) : null}
                </span>
                <>
                  {offer.status === 'expired' ? (
                    <IconRcOfferReturnedIcon className={cx(isMe && styles.reverseIcon)} />
                  ) : null}
                  {offer.status === 'uncharged' ? (
                    <>
                      <IconRcOfferUnchargedIcon />
                      <HelperTooltip title="Offer Boost">
                        <div className={styles.boostIcon}>
                          <IconRcHiBoost onClick={handleBoostClick} width={16} height={16} />
                        </div>
                      </HelperTooltip>
                    </>
                  ) : null}
                  {offer.status === 'charged' ? <IconRcOfferChargedMeIcon /> : null}
                </>
              </span>
            </HelperTooltip>
          ) : null}
        </div>

        {isScamMessageUnread ? (
          <div className={styles.scamMask}>
            <span className={styles.title}>{t('NormalMessageItem.1x3vg5f')}</span>
            <span className={styles.openButton} onClick={readScamMessage}>
              {t('NormalMessageItem.2njt9e')}
            </span>
          </div>
        ) : null}

        {isScamMessage ? <div className={styles.scamTag}>!</div> : null}
        {boostShown && offer?.id ? <BoostTipModal offerId={offer.id} onClose={hideBoost} /> : null}
      </div>
    </div>
  );
}
